import { useState, useCallback, useEffect, useRef } from 'react'
import { toast } from 'react-toastify'
import UserService from './../../../../shared/services/User.service'
import UserProfileService from './../../../../shared/services/UserProfile.service'
import EchoService from './../../../../shared/services/Echo.service'

const getViewedUserIdFromUrl = () => {
  if (typeof window === 'undefined') return null
  const match = window.location.pathname.match(/^\/(\d+)\/account-profile/i)
  return match ? match[1] : null
}

/**
 * @param {Function} onHeadshotChange - called with the new u_picture after a refinement completes or is reverted
 */
export const useHeadshotRefinement = onHeadshotChange => {
  const [refinementStatus, setRefinementStatus] = useState(null) // null | 'processing' | 'true' | 'false' | 'failed'
  const [isRefineLoading, setIsRefineLoading] = useState(false)
  const [isRevertLoading, setIsRevertLoading] = useState(false)
  const channelRef = useRef(null)
  const onHeadshotChangeRef = useRef(onHeadshotChange)
  onHeadshotChangeRef.current = onHeadshotChange

  const getUserId = useCallback(() => {
    const viewedUserId = getViewedUserIdFromUrl()
    return viewedUserId || UserProfileService.getUserId()
  }, [])

  const applyHeadshot = useCallback(
    (userId, picture) => {
      if (!picture) return
      if (parseInt(userId, 10) === parseInt(UserProfileService.getUserId(), 10)) UserProfileService.set('u_picture', picture)
      onHeadshotChangeRef.current?.(picture)
    },
    []
  )

  // Fetch refinement status on mount
  useEffect(() => {
    const userId = getUserId()
    if (!userId) return

    UserService.getUserMetas('headshot---ai-refined', userId)
      .then(metas => {
        if (metas && metas.length > 0) {
          setRefinementStatus(metas[0].meta_value)
        } else {
          setRefinementStatus('false')
        }
      })
      .catch(() => {
        setRefinementStatus('false')
      })
  }, [getUserId])

  // Subscribe to websocket for refinement completion
  useEffect(() => {
    const userId = getUserId()
    if (!userId) return

    let mounted = true

    EchoService.init()
      .then(echo => {
        if (!mounted || !echo) return

        channelRef.current = echo
          .private(`headshots.refinement.${userId}`)
          .listen('.headshots.refinement.completed', payload => {
            setRefinementStatus('true')
            applyHeadshot(userId, payload?.headshot_path)
            toast.success('Your headshot has been refined!', {
              position: toast.POSITION.TOP_RIGHT,
            })
          })
          .listen('.headshots.refinement.failed', payload => {
            setRefinementStatus('failed')
            toast.error(payload?.message || "We couldn't refine your headshot. Please try again later.", {
              position: toast.POSITION.TOP_RIGHT,
            })
          })
      })
      .catch(err => {
        console.warn('HeadshotRefinement: Echo init failed', err)
      })

    return () => {
      mounted = false
      if (window.Echo) {
        try {
          window.Echo.leaveChannel(`private-headshots.refinement.${userId}`)
        } catch (e) {
          /* no-op */
        }
      }
    }
  }, [getUserId, applyHeadshot])

  const isRefined = refinementStatus === 'true'
  const isProcessing = refinementStatus === 'processing'
  const canRefine = refinementStatus !== null && refinementStatus !== 'true' && refinementStatus !== 'processing'

  const refineHeadshot = useCallback(async () => {
    const userId = getUserId()
    if (!userId) return false

    setIsRefineLoading(true)

    try {
      await UserService.refineHeadshot(userId)
      setRefinementStatus('processing')
      return true
    } catch (err) {
      console.error('Failed to trigger headshot refinement:', err)
      toast.error(err?.message || 'Failed to start headshot refinement. Please try again later.', {
        position: toast.POSITION.TOP_RIGHT,
      })
      return false
    } finally {
      setIsRefineLoading(false)
    }
  }, [getUserId])

  const revertHeadshot = useCallback(async () => {
    const userId = getUserId()
    if (!userId) return null

    setIsRevertLoading(true)

    try {
      const result = await UserService.revertHeadshot(userId)
      setRefinementStatus('false')

      applyHeadshot(userId, result?.u_picture)

      toast.success('Headshot reverted to original.', {
        position: toast.POSITION.TOP_RIGHT,
      })

      return result
    } catch (err) {
      console.error('Failed to revert headshot:', err)
      toast.error('Failed to revert headshot. Please try again.', {
        position: toast.POSITION.TOP_RIGHT,
      })
      return null
    } finally {
      setIsRevertLoading(false)
    }
  }, [getUserId, applyHeadshot])

  const resetRefinementStatus = useCallback(() => {
    setRefinementStatus('false')
  }, [])

  return {
    refinementStatus,
    isRefined,
    isProcessing,
    canRefine,
    isRefineLoading,
    isRevertLoading,
    refineHeadshot,
    revertHeadshot,
    resetRefinementStatus,
  }
}
